export type PostType = "feed" | "story" | "reel" | "carousel"
export type PostStatus = "draft" | "scheduled" | "publishing" | "published" | "failed"

export type MediaAttachment = {
  id: number
  url: string
  content_type: string
  byte_size: number
}

// 予約投稿に保存済みのリール音源（`scheduled_posts#show` の `audio` サブオブジェクト）。
// アートワーク・試聴 URL は約1.5日で失効するため保存されず、ここには含まれない
// （表示用のメタは `audios#index` / `#show` の Audio 型から取る）。
export type ScheduledPostAudio = {
  audio_id: string
  title: string | null
  artist: string | null
  audio_type: string | null
  duration_in_ms: number | null
  audio_volume: number
  video_volume: number
}

// 音源検索・メタ取得（`audios#index` / `audios#show`）のレスポンス 1 件。
// `cover_artwork_url` / `preview_url` は音源によって null になりうる（Meta が null のキーを
// 省略するため。特にトレンド音源のほとんどは `preview_url` を持たない）。
//
// 試聴系は 2 種類あり性質が違う（サーバー側 Instagram::AudioPresenter のコメントも参照）。
// - `preview_url`: 音声ファイルの URL。`<audio>` に渡してインライン再生できる
// - `instagram_url`: Instagram の音源ページ（Web ページ）の URL。**再生はできない**ので
//   外部リンクとして開く用途のみ。実測上この 2 つはほぼ排他で、トレンド一覧では大半が後者
export type Audio = {
  audio_id: string
  title: string | null
  artist: string | null
  audio_type: string | null
  duration_in_ms: number | null
  cover_artwork_url: string | null
  preview_url: string | null
  instagram_url: string | null
}

export type AudioType = "music" | "original_sound"

export type ScheduledPost = {
  id: number
  title: string | null
  post_type: PostType
  status: PostStatus
  caption: string | null
  scheduled_at: string | null
  published_at: string | null
  category_id: number | null
  category_name: string | null
  media_attachments: MediaAttachment[]
  post_id: number | null
  error_message: string | null
  instagram_account: { id: number; username: string }
  created_by: { id: number; name: string }
  // 音源なしは null。リール以外では常に null（サーバー側で構造的に保証されている）
  audio: ScheduledPostAudio | null
}

// ─── Instagram メディア制約 ───

export const ALLOWED_IMAGE_TYPES: Record<PostType, string[]> = {
  feed:     ["image/jpeg", "image/png"],
  story:    ["image/jpeg", "image/png"],
  reel:     ["image/jpeg", "image/png"],
  carousel: ["image/jpeg", "image/png"],
}
export const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/quicktime"]

export const IMAGE_MAX_BYTES = 8 * 1024 * 1024   // 8MB

export const VIDEO_MAX_BYTES: Record<PostType, number> = {
  feed:     100 * 1024 * 1024,  // 100MB
  story:    100 * 1024 * 1024,  // 100MB
  reel:     100 * 1024 * 1024,  // 100MB
  carousel: 100 * 1024 * 1024,  // 100MB
}

export const VIDEO_MIN_DURATION: Record<PostType, number> = { feed: 3, story: 3, reel: 3,  carousel: 3  }
export const VIDEO_MAX_DURATION: Record<PostType, number> = { feed: 90, story: 60, reel: 90, carousel: 60 }

// ─── 解像度制約 ───

export const IMAGE_MIN_WIDTH = 320
export const IMAGE_MAX_WIDTH = 1440
export const VIDEO_MIN_WIDTH = 540
export const VIDEO_MIN_HEIGHT = 960
export const VIDEO_MAX_WIDTH = 1920

// ─── アスペクト比制約（null = 制限なし） ───

export const IMAGE_ASPECT_RATIO_MIN: Record<PostType, number | null> = {
  feed:     0.8,
  story:    null,
  reel:     null,
  carousel: 0.8,
}
export const IMAGE_ASPECT_RATIO_MAX: Record<PostType, number | null> = {
  feed:     1.91,
  story:    null,
  reel:     null,
  carousel: 1.91,
}
export const VIDEO_ASPECT_RATIO_MIN: Record<PostType, number | null> = {
  feed:     null,
  story:    0.1,
  reel:     0.01,
  carousel: null,
}
export const VIDEO_ASPECT_RATIO_MAX: Record<PostType, number | null> = {
  feed:     null,
  story:    10.0,
  reel:     10.0,
  carousel: null,
}

export const CAROUSEL_TOTAL_MAX_BYTES = 400 * 1024 * 1024  // 400MB
